import React, { useEffect, useRef, useState } from "react";
import { _ } from "libs";
import { getCookieLang, getShortLang } from "../libs/detectLanguage";

type props = {
	callback: any;
};

type Step = "idle" | "needsPopup" | "waitingForPopup";

const POPUP_MESSAGE_SOURCE = "gotcourts";
const POPUP_MESSAGE_EVENT = "storage_access_interacted";

const getPopupUrl = (): string => {
	const lang = getCookieLang() || getShortLang();
	return `${window.location.origin}${process.env.REACT_APP_BASE_PATH || ""}/storage-access.html?lang=${lang}`;
};

const requestStorageAccess = async (): Promise<boolean> => {
	//@ts-ignore
	if (!(document.hasStorageAccess && document.requestStorageAccess)) {
		return false;
	}
	try {
		//@ts-ignore
		await document.requestStorageAccess();
		return true;
	} catch (e) {
		return false;
	}
};

/**
 * Shown inside the widget iframe when the browser blocks our cookies.
 *
 * 1. First click: ask for storage access directly (works once the user has visited
 *    apps.gotcourts.com as a top-level page before).
 * 2. If the browser denies that, show a second button that opens a popup on our own origin.
 *    The popup must be opened synchronously inside its own click: Safari drops the user
 *    activation across the awaited requestStorageAccess() call and Chrome consumes it when
 *    it prompts, so a popup opened after the denial is blocked by the popup blocker.
 *    One click in the popup counts as top-level interaction, after which browsers grant the
 *    iframe cookie access.
 * 3. When the popup closes, reload the widget so the app boots with cookies enabled. Should
 *    the browser still refuse, the next click here now gets a real prompt instead of a denial.
 */
const ActivateCookies = ({ callback }: props) => {
	const [step, setStep] = useState<Step>("idle");
	const popupRef = useRef<Window | null>(null);
	const reloadedRef = useRef(false);

	const reload = () => {
		if (reloadedRef.current) return;
		reloadedRef.current = true;
		window.location.reload();
	};

	useEffect(() => {
		const onMessage = (event: MessageEvent) => {
			if (event.origin !== window.location.origin) return;
			const data = event.data || {};
			if (data.source === POPUP_MESSAGE_SOURCE && data.event === POPUP_MESSAGE_EVENT) {
				reload();
			}
		};
		window.addEventListener("message", onMessage);
		return () => window.removeEventListener("message", onMessage);
	}, []);

	useEffect(() => {
		if (step !== "waitingForPopup") return;
		// Fallback when postMessage never arrives: reload once the popup has been closed.
		const timer = window.setInterval(() => {
			if (!popupRef.current || popupRef.current.closed) {
				window.clearInterval(timer);
				reload();
			}
		}, 500);
		return () => window.clearInterval(timer);
	}, [step]);

	// Must be the first thing that runs in the click handler, see the component comment.
	const openPopup = () => {
		const popup = window.open(getPopupUrl(), "gotcourts-storage-access", "popup,width=480,height=320");
		if (!popup) {
			// Blocked anyway: stay on this button so the user can allow popups and try again.
			setStep("needsPopup");
			return;
		}
		popupRef.current = popup;
		setStep("waitingForPopup");
	};

	const activate = async () => {
		if (await requestStorageAccess()) {
			callback();
			return;
		}
		// Denied: the user has never interacted with our origin top-level. Ask for a second
		// click so the popup can be opened with a fresh user activation.
		setStep("needsPopup");
	};

	return (
		<div className="layout">
			<div className="fullPageWarning">
				<h1>{_("bookingWidget.allowStorageAccess.header")}</h1>
				{step === "waitingForPopup" ? (
					<p>{_("bookingWidget.allowStorageAccess.popupHint")}</p>
				) : (
					<button
						id="allow-cookies"
						className="button submit"
						type="button"
						onClick={step === "needsPopup" ? openPopup : activate}
					>
						{_(
							step === "needsPopup"
								? "bookingWidget.allowStorageAccess.openWindow"
								: "bookingWidget.allowStorageAccess.button"
						)}
					</button>
				)}
			</div>
		</div>
	);
};

export default ActivateCookies;
